import { supabase } from './supabaseClient';
import { getWebHotelierProxyService } from '../features/webhotelier/services/webHotelierProxyService';
import { assertPropertyAccess } from './propertyAuthorization';
import { fetchGuestPiiMap } from './reservationGuestPii';

export interface ReservationData {
  id: string;
  roomTypeId: string;
  checkIn: Date;
  checkOut: Date;
  rate: number;
  guests: number;
  status: 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled';
  propertyId: string;
  guestName?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface ReservationMetrics {
  occupancyRate: number;
  adr: number;
  revpar: number;
  totalBookings: number;
  occupancyChange: number;
  adrChange: number;
  revparChange: number;
  bookingsChange: number;
}

/**
 * Service for managing reservation data and calculating metrics
 */
export class ReservationService {
  private cache = new Map<string, ReservationData[]>();
  private cacheExpiry = new Map<string, number>();
  private metricsCache = new Map<
    string,
    { data: ReservationMetrics; timestamp: number }
  >();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get reservations for a property with optional filtering
   */
  async getReservations(filters: {
    propertyId: string;
    dateRange?: { start: Date; end: Date };
    roomTypeIds?: string[];
    status?: ReservationData['status'][];
  }): Promise<ReservationData[]> {
    await assertPropertyAccess(filters.propertyId);

    const cacheKey = this.generateCacheKey(filters);

    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey) || [];
    }

    try {
      let query = supabase
        .from('reservations')
        .select(
          `
          id,
          room_type_id,
          check_in,
          check_out,
          rate,
          guests,
          status,
          property_id,
          created_at,
          updated_at
        `
        )
        .eq('property_id', filters.propertyId);

      // Apply filters
      if (filters.dateRange) {
        query = query
          .gte('check_in', filters.dateRange.start.toISOString().split('T')[0])
          .lte('check_out', filters.dateRange.end.toISOString().split('T')[0]);
      }

      if (filters.roomTypeIds && filters.roomTypeIds.length > 0) {
        query = query.in('room_type_id', filters.roomTypeIds);
      }

      if (filters.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching reservations:', error);
        // Return cached data if available, even if expired
        return this.cache.get(cacheKey) || [];
      }

      const rows = data || [];
      const piiMap = await fetchGuestPiiMap(
        filters.propertyId,
        rows.map((r) => r.id)
      );

      const reservations: ReservationData[] = rows.map((item) => ({
        id: item.id,
        roomTypeId: item.room_type_id,
        checkIn: new Date(item.check_in),
        checkOut: new Date(item.check_out),
        rate: item.rate,
        guests: item.guests,
        status: item.status,
        propertyId: item.property_id,
        guestName: piiMap.get(item.id)?.name ?? undefined,
        createdAt: new Date(item.created_at),
        updatedAt: item.updated_at ? new Date(item.updated_at) : undefined,
      }));

      // Update cache
      this.cache.set(cacheKey, reservations);
      this.cacheExpiry.set(cacheKey, Date.now() + this.CACHE_DURATION);

      return reservations;
    } catch (error) {
      console.error('Error in getReservations:', error);
      // Return cached data if available
      return this.cache.get(cacheKey) || [];
    }
  }

  /**
   * Get enhanced metrics with WebHotelier data
   */
  async getEnhancedMetrics(
    propertyId: string,
    totalRooms: number
  ): Promise<ReservationMetrics> {
    await assertPropertyAccess(propertyId);

    const cacheKey = `enhanced-metrics-${propertyId}`;
    const cached = this.metricsCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      // Try WebHotelier data first
      const webHotelierMetrics = await this.getWebHotelierMetrics(
        propertyId,
        totalRooms
      );
      if (webHotelierMetrics) {
        this.metricsCache.set(cacheKey, {
          data: webHotelierMetrics,
          timestamp: Date.now(),
        });
        return webHotelierMetrics;
      }

      // Fallback to local calculation
      const localMetrics = await this.calculateMetrics(propertyId, totalRooms);
      this.metricsCache.set(cacheKey, {
        data: localMetrics,
        timestamp: Date.now(),
      });
      return localMetrics;
    } catch (error) {
      console.error('Error getting enhanced metrics:', error);
      return await this.calculateMetrics(propertyId, totalRooms);
    }
  }

  /**
   * Get metrics from WebHotelier calendar API (accurate booked/total data)
   */
  private async getWebHotelierMetrics(
    propertyId: string,
    _totalRooms: number
  ): Promise<ReservationMetrics | null> {
    try {
      // Check if WebHotelier integration is configured
      const integration = await this.getWebHotelierIntegration(propertyId);
      if (!integration?.propertyCode) {
        return null;
      }

      const webHotelierService = getWebHotelierProxyService();
      if (!webHotelierService) {
        return null;
      }

      // WebHotelier calendar API only supports current month or later
      // Use first day of current month as the earliest allowed date
      const today = new Date();
      const currentMonthStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );

      // For current period, use current month data only (API limitation)
      const fromDate = currentMonthStart.toISOString().split('T')[0] ?? '';
      const toDate = today.toISOString().split('T')[0] ?? '';

      // Fetch current period calendar (current month only due to API limitation)
      const currentResult = await webHotelierService.getAvailabilityCalendar({
        propertyCode: integration.propertyCode,
        from: fromDate,
        to: toDate,
      });

      if (!currentResult.success || !currentResult.data?.calendar) {
        console.log('[ReservationService] Calendar API returned no data');
        return null;
      }

      // Previous period comparison not available via calendar API (historical data not supported)
      // Would need to use booking/reservation data for historical comparison
      const previousResult = { success: false as const, data: null };

      // Calculate metrics from calendar data
      const currentMetrics = this.calculateMetricsFromCalendar(
        currentResult.data.calendar
      );
      const previousMetrics = previousResult.success
        ? this.calculateMetricsFromCalendar(previousResult.data?.calendar || [])
        : null;

      return {
        occupancyRate: currentMetrics.occupancyRate,
        adr: currentMetrics.adr,
        revpar: currentMetrics.revpar,
        totalBookings: currentMetrics.totalBookings,
        occupancyChange: previousMetrics
          ? this.calculatePercentageChange(
              currentMetrics.occupancyRate,
              previousMetrics.occupancyRate
            )
          : 0,
        adrChange: previousMetrics
          ? this.calculatePercentageChange(
              currentMetrics.adr,
              previousMetrics.adr
            )
          : 0,
        revparChange: previousMetrics
          ? this.calculatePercentageChange(
              currentMetrics.revpar,
              previousMetrics.revpar
            )
          : 0,
        bookingsChange: previousMetrics
          ? this.calculatePercentageChange(
              currentMetrics.totalBookings,
              previousMetrics.totalBookings
            )
          : 0,
      };
    } catch (error) {
      console.error('Error fetching WebHotelier metrics:', error);
      return null;
    }
  }

  /**
   * Get WebHotelier integration config for a property
   */
  private async getWebHotelierIntegration(
    propertyId: string
  ): Promise<{ propertyCode: string } | null> {
    try {
      const { data, error } = await supabase
        .from('pms_integrations')
        .select('config')
        .eq('property_id', propertyId)
        .eq('pms_type', 'webhotelier')
        .eq('active', true)
        .single();

      if (error || !data) return null;

      const config = data.config as { propertyCode?: string } | null;
      if (!config?.propertyCode) return null;

      return { propertyCode: config.propertyCode };
    } catch {
      return null;
    }
  }

  /**
   * Calculate metrics from WebHotelier calendar data
   * Calendar data has accurate booked/total/rates per room type per day
   */
  private calculateMetricsFromCalendar(
    calendar: Array<{
      date: string;
      roomTypes: {
        [roomCode: string]: {
          available: number;
          booked: number;
          total: number;
          rates?: {
            [rateCode: string]: {
              price: number;
              available: boolean;
            };
          };
        };
      };
    }>
  ): {
    occupancyRate: number;
    adr: number;
    revpar: number;
    totalBookings: number;
    totalRevenue: number;
  } {
    let totalBooked = 0;
    let totalCapacity = 0;
    let totalRevenue = 0;
    let _daysWithBookings = 0;

    for (const day of calendar) {
      let dayBooked = 0;
      let dayCapacity = 0;
      let dayRevenue = 0;

      for (const roomData of Object.values(day.roomTypes)) {
        const booked = roomData.booked || 0;
        const total = roomData.total || 0;

        dayBooked += booked;
        dayCapacity += total;

        // Calculate revenue from booked rooms × rate
        if (roomData.rates && booked > 0) {
          // Use the first available rate (typically BAR - Best Available Rate)
          const rateValues = Object.values(roomData.rates);
          if (rateValues.length > 0) {
            const rate = rateValues[0].price || 0;
            dayRevenue += booked * rate;
          }
        }
      }

      totalBooked += dayBooked;
      totalCapacity += dayCapacity;
      totalRevenue += dayRevenue;

      if (dayBooked > 0) {
        _daysWithBookings++;
      }
    }

    // Calculate metrics
    const occupancyRate =
      totalCapacity > 0 ? (totalBooked / totalCapacity) * 100 : 0;
    const adr = totalBooked > 0 ? totalRevenue / totalBooked : 0;
    const revpar = totalCapacity > 0 ? totalRevenue / totalCapacity : 0;

    return {
      occupancyRate: Math.round(occupancyRate * 10) / 10,
      adr: Math.round(adr * 100) / 100,
      revpar: Math.round(revpar * 100) / 100,
      totalBookings: totalBooked,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
    };
  }

  /**
   * Calculate percentage change between two values
   */
  private calculatePercentageChange(current: number, previous: number): number {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  }

  /**
   * Calculate reservation metrics for dashboard (original method)
   */
  async calculateMetrics(
    propertyId: string,
    totalRooms: number
  ): Promise<ReservationMetrics> {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(today.getDate() - 60);

    try {
      // Get current period reservations (last 30 days)
      const currentPeriodReservations = await this.getReservations({
        propertyId,
        dateRange: { start: thirtyDaysAgo, end: today },
        status: ['confirmed', 'checked_in', 'checked_out'],
      });

      // Get previous period reservations (30-60 days ago)
      const previousPeriodReservations = await this.getReservations({
        propertyId,
        dateRange: { start: sixtyDaysAgo, end: thirtyDaysAgo },
        status: ['confirmed', 'checked_in', 'checked_out'],
      });

      // Calculate current metrics
      const daysInPeriod = 30;
      const totalRoomNights = totalRooms * daysInPeriod;

      const currentOccupiedNights = this.calculateOccupiedNights(
        currentPeriodReservations,
        daysInPeriod
      );
      const currentOccupancyRate =
        (currentOccupiedNights / totalRoomNights) * 100;

      const currentRevenue = this.calculateRevenue(
        currentPeriodReservations,
        daysInPeriod
      );
      const currentADR =
        currentOccupiedNights > 0 ? currentRevenue / currentOccupiedNights : 0;
      const currentRevPAR = currentRevenue / totalRoomNights;

      // Calculate previous metrics for comparison
      const previousOccupiedNights = this.calculateOccupiedNights(
        previousPeriodReservations,
        daysInPeriod
      );
      const previousOccupancyRate =
        (previousOccupiedNights / totalRoomNights) * 100;

      const previousRevenue = this.calculateRevenue(
        previousPeriodReservations,
        daysInPeriod
      );
      const previousADR =
        previousOccupiedNights > 0
          ? previousRevenue / previousOccupiedNights
          : 0;
      const previousRevPAR = previousRevenue / totalRoomNights;

      // Calculate percentage changes
      const occupancyChange =
        previousOccupancyRate > 0
          ? ((currentOccupancyRate - previousOccupancyRate) /
              previousOccupancyRate) *
            100
          : 0;

      const adrChange =
        previousADR > 0 ? ((currentADR - previousADR) / previousADR) * 100 : 0;

      const revparChange =
        previousRevPAR > 0
          ? ((currentRevPAR - previousRevPAR) / previousRevPAR) * 100
          : 0;

      const bookingsChange =
        previousPeriodReservations.length > 0
          ? ((currentPeriodReservations.length -
              previousPeriodReservations.length) /
              previousPeriodReservations.length) *
            100
          : 0;

      return {
        occupancyRate: currentOccupancyRate,
        adr: currentADR,
        revpar: currentRevPAR,
        totalBookings: currentPeriodReservations.length,
        occupancyChange,
        adrChange,
        revparChange,
        bookingsChange,
      };
    } catch (error) {
      console.error('Error calculating metrics:', error);

      // Return default metrics on error
      return {
        occupancyRate: 0,
        adr: 0,
        revpar: 0,
        totalBookings: 0,
        occupancyChange: 0,
        adrChange: 0,
        revparChange: 0,
        bookingsChange: 0,
      };
    }
  }

  /**
   * Calculate occupied nights from reservations
   */
  private calculateOccupiedNights(
    reservations: ReservationData[],
    maxDays: number
  ): number {
    return reservations.reduce((sum, res) => {
      const nights = Math.ceil(
        (res.checkOut.getTime() - res.checkIn.getTime()) / (1000 * 60 * 60 * 24)
      );
      return sum + Math.min(nights, maxDays);
    }, 0);
  }

  /**
   * Calculate total revenue from reservations
   */
  private calculateRevenue(
    reservations: ReservationData[],
    maxDays: number
  ): number {
    return reservations.reduce((sum, res) => {
      const nights = Math.ceil(
        (res.checkOut.getTime() - res.checkIn.getTime()) / (1000 * 60 * 60 * 24)
      );
      return sum + res.rate * Math.min(nights, maxDays);
    }, 0);
  }

  /**
   * Generate cache key for reservation filters
   */
  private generateCacheKey(filters: {
    propertyId: string;
    dateRange?: { start: Date; end: Date };
    roomTypeIds?: string[];
    status?: ReservationData['status'][];
  }): string {
    return JSON.stringify({
      propertyId: filters.propertyId,
      dateRange: filters.dateRange
        ? {
            start: filters.dateRange.start.toISOString().split('T')[0],
            end: filters.dateRange.end.toISOString().split('T')[0],
          }
        : null,
      roomTypeIds: filters.roomTypeIds?.sort(),
      status: filters.status?.sort(),
    });
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(cacheKey: string): boolean {
    const expiry = this.cacheExpiry.get(cacheKey);
    return (
      expiry !== undefined && Date.now() < expiry && this.cache.has(cacheKey)
    );
  }

  /**
   * Clear cache for specific property or all data
   */
  clearCache(propertyId?: string): void {
    if (propertyId) {
      // Clear cache entries that match the property
      for (const key of this.cache.keys()) {
        if (key.includes(`"propertyId":"${propertyId}"`)) {
          this.cache.delete(key);
          this.cacheExpiry.delete(key);
        }
      }
    } else {
      // Clear all cache
      this.cache.clear();
      this.cacheExpiry.clear();
    }
  }

  /**
   * Get reservation trends data for charts
   */
  async getReservationTrends(
    propertyId: string,
    days: number = 30
  ): Promise<
    Array<{
      date: string;
      occupancy: number;
      revenue: number;
      bookings: number;
    }>
  > {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);

    try {
      const reservations = await this.getReservations({
        propertyId,
        dateRange: { start: startDate, end: endDate },
        status: ['confirmed', 'checked_in', 'checked_out'],
      });

      // Group by date
      const dailyData = new Map<
        string,
        {
          occupancy: number;
          revenue: number;
          bookings: number;
        }
      >();

      // Initialize all dates
      for (
        let d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        const dateStr = d.toISOString().split('T')[0];
        dailyData.set(dateStr, { occupancy: 0, revenue: 0, bookings: 0 });
      }

      // Process reservations
      reservations.forEach((reservation) => {
        const checkInDate = reservation.checkIn.toISOString().split('T')[0];
        const data = dailyData.get(checkInDate);

        if (data) {
          data.bookings += 1;
          data.revenue += reservation.rate;
          data.occupancy += 1; // Simplified - could be calculated more precisely
        }
      });

      // Convert to array
      return Array.from(dailyData.entries())
        .map(([date, data]) => ({ date, ...data }))
        .sort((a, b) => a.date.localeCompare(b.date));
    } catch (error) {
      console.error('Error getting reservation trends:', error);
      return [];
    }
  }

  /**
   * Get monthly revenue trends for past N months
   * Tries WebHotelier API first, falls back to local reservations
   */
  async getMonthlyRevenueTrends(
    propertyId: string,
    months: number = 12
  ): Promise<
    Array<{
      month: string;
      revenue: number;
      adr: number;
      occupancy: number;
      roomNights: number;
    }>
  > {
    await assertPropertyAccess(propertyId);

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Try to get data from WebHotelier API first
    try {
      const apiData = await this.getMonthlyRevenueFromAPI(propertyId, months);
      if (apiData && apiData.length > 0 && apiData.some((d) => d.revenue > 0)) {
        console.log(
          '[ReservationService] Using WebHotelier API data for monthly revenue'
        );
        return apiData;
      }
    } catch (_error) {
      console.log('[ReservationService] API fetch failed, using local data');
    }

    // Fallback to local reservations data
    return this.getMonthlyRevenueFromLocal(propertyId, months, monthNames);
  }

  /**
   * Get monthly revenue from WebHotelier Calendar API
   * NOTE: WebHotelier calendar API only supports current month or later.
   * Historical data must come from booking/reservation APIs or local storage.
   */
  private async getMonthlyRevenueFromAPI(
    propertyId: string,
    months: number
  ): Promise<
    Array<{
      month: string;
      revenue: number;
      adr: number;
      occupancy: number;
      roomNights: number;
    }>
  > {
    const integration = await this.getWebHotelierIntegration(propertyId);
    if (!integration?.propertyCode) {
      return [];
    }

    const webHotelierService = getWebHotelierProxyService();
    if (!webHotelierService) {
      return [];
    }

    // WebHotelier calendar API only supports current month or later
    // Use the first day of current month as the earliest allowed date
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    // Calculate the requested date range
    const endDate = new Date();
    const requestedStartDate = new Date();
    requestedStartDate.setMonth(requestedStartDate.getMonth() - months + 1);
    requestedStartDate.setDate(1);

    // If the entire requested range is in the past, return empty (use fallback)
    if (requestedStartDate < currentMonthStart && endDate < currentMonthStart) {
      console.log(
        '[ReservationService] Requested date range is entirely in the past, using local fallback'
      );
      return [];
    }

    // Adjust start date to current month if it's in the past
    const startDate =
      requestedStartDate < currentMonthStart
        ? currentMonthStart
        : requestedStartDate;

    console.log(
      `[ReservationService] Fetching calendar data from ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`
    );

    // Fetch calendar data for the valid range only
    const fromDate = startDate.toISOString().split('T')[0] ?? '';
    const toDate = endDate.toISOString().split('T')[0] ?? '';
    const result = await webHotelierService.getAvailabilityCalendar({
      propertyCode: integration.propertyCode,
      from: fromDate,
      to: toDate,
    });

    if (!result.success || !result.data?.calendar) {
      return [];
    }

    // Aggregate by month
    const monthlyData = new Map<
      string,
      {
        revenue: number;
        bookedRooms: number;
        totalRooms: number;
        days: number;
      }
    >();

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Initialize months
    for (let i = 0; i < months; i++) {
      const date = new Date(startDate);
      date.setMonth(date.getMonth() + i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData.set(monthKey, {
        revenue: 0,
        bookedRooms: 0,
        totalRooms: 0,
        days: 0,
      });
    }

    // Process calendar data
    for (const day of result.data.calendar) {
      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthData = monthlyData.get(monthKey);

      if (monthData) {
        monthData.days += 1;

        for (const roomData of Object.values(day.roomTypes)) {
          const booked = roomData.booked || 0;
          const total = roomData.total || 0;

          monthData.bookedRooms += booked;
          monthData.totalRooms += total;

          // Calculate revenue from booked rooms × rate
          if (roomData.rates && booked > 0) {
            const rateValues = Object.values(roomData.rates);
            const firstRate = rateValues[0];
            if (firstRate) {
              const rate = firstRate.price || 0;
              monthData.revenue += booked * rate;
            }
          }
        }
      }
    }

    // Format results
    return Array.from(monthlyData.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, data]) => {
        const [, monthStr] = key.split('-');
        const monthIndex = parseInt(monthStr ?? '1', 10) - 1;
        const occupancy =
          data.totalRooms > 0
            ? Math.round((data.bookedRooms / data.totalRooms) * 100)
            : 0;
        const adr =
          data.bookedRooms > 0
            ? Math.round((data.revenue / data.bookedRooms) * 100) / 100
            : 0;

        return {
          month: monthNames[monthIndex] ?? 'Unknown',
          revenue: Math.round(data.revenue),
          adr,
          occupancy,
          roomNights: data.bookedRooms,
        };
      });
  }

  /**
   * Get monthly revenue from local reservations (fallback)
   */
  private async getMonthlyRevenueFromLocal(
    propertyId: string,
    months: number,
    monthNames: string[]
  ): Promise<
    Array<{
      month: string;
      revenue: number;
      adr: number;
      occupancy: number;
      roomNights: number;
    }>
  > {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months + 1);
    startDate.setDate(1);

    try {
      const reservations = await this.getReservations({
        propertyId,
        dateRange: { start: startDate, end: endDate },
        status: ['confirmed', 'checked_in', 'checked_out'],
      });

      // Initialize all months
      const monthlyData = new Map<
        string,
        { revenue: number; roomNights: number; bookings: number }
      >();

      for (let i = 0; i < months; i++) {
        const date = new Date(startDate);
        date.setMonth(date.getMonth() + i);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData.set(monthKey, { revenue: 0, roomNights: 0, bookings: 0 });
      }

      // Process reservations
      for (const reservation of reservations) {
        const nights = Math.ceil(
          (reservation.checkOut.getTime() - reservation.checkIn.getTime()) /
            (1000 * 60 * 60 * 24)
        );
        const monthKey = `${reservation.checkIn.getFullYear()}-${String(reservation.checkIn.getMonth() + 1).padStart(2, '0')}`;

        const existing = monthlyData.get(monthKey);
        if (existing) {
          existing.revenue += reservation.rate * nights;
          existing.roomNights += nights;
          existing.bookings += 1;
        }
      }

      // Format results
      return Array.from(monthlyData.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, data]) => {
          const [, monthStr] = key.split('-');
          const monthIndex = parseInt(monthStr ?? '1', 10) - 1;
          return {
            month: monthNames[monthIndex] ?? 'Unknown',
            revenue: Math.round(data.revenue),
            adr:
              data.roomNights > 0
                ? Math.round(data.revenue / data.roomNights)
                : 0,
            occupancy: 0, // Would need total rooms per property to calculate
            roomNights: data.roomNights,
          };
        });
    } catch (error) {
      console.error('Error getting monthly revenue trends:', error);
      return [];
    }
  }
}

// Export singleton instance
export const reservationService = new ReservationService();
